import React from 'react';
import { FJ710 } from '../../../services/c209Api';
import { dateToInput, inputToDate } from '../helpers';

interface Tab1Props {
  formData: Partial<FJ710>;
  names: Record<string, string>;
  isEditing: boolean;
  getField: (key: keyof FJ710) => FJ710[keyof FJ710];
  setField: (key: keyof FJ710, value: FJ710[keyof FJ710]) => void;
  resolveSingleName: (nameKey: string, type: string, code: string) => void;
}

const Tab1BasicInfo: React.FC<Tab1Props> = React.memo(({
  formData, names, isEditing, getField, setField, resolveSingleName,
}) => (
  <div className="tab-content-section">
    <h3 className="section-title">基本資料</h3>
    <div className="form-grid">
      {/* Row 1 */}
      <label>申請單號</label>
      <input type="text" value={getField('fj710_01') as string || ''} disabled style={{ width: 140 }} />

      <label>申請日期</label>
      <input
        type="date"
        value={dateToInput(getField('fj710_11') as string)}
        onChange={e => setField('fj710_11', inputToDate(e.target.value) as string)}
        disabled={!isEditing}
        style={{ width: 140 }}
      />

      {/* Row 2 */}
      <label>來樣單號</label>
      <input
        type="text"
        value={getField('fj710_12') as string || ''}
        onChange={e => setField('fj710_12', e.target.value)}
        disabled={!isEditing}
        style={{ width: 140 }}
      />

      {/* BR-U04: Dynamic customer name display */}
      <label>客戶名稱</label>
      <span className="lookup-display">{names.customer || ''}</span>

      {/* Row 3 */}
      <label className="required">申請部門</label>
      <div className="lookup-field">
        <input
          type="text"
          value={getField('fj710_13') as string || ''}
          onChange={e => setField('fj710_13', e.target.value)}
          onBlur={() => resolveSingleName('department', 'department', getField('fj710_13') as string)}
          disabled={!isEditing}
          style={{ width: 80 }}
        />
        <span className="lookup-name">{names.department || ''}</span>
      </div>

      <label className="required">樣品需要日期</label>
      <input
        type="date"
        value={dateToInput(getField('fj710_14') as string)}
        onChange={e => setField('fj710_14', inputToDate(e.target.value) as string)}
        disabled={!isEditing}
        style={{ width: 140 }}
      />

      {/* Row 4: Sample Quantity */}
      <label>樣品需要量</label>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
        <input
          type="number"
          value={getField('fj710_151') as number || 0}
          onChange={e => setField('fj710_151', parseFloat(e.target.value) || 0)}
          disabled={!isEditing}
          style={{ width: 100 }}
        />
        <input
          type="text"
          value={getField('fj710_152') as string || ''}
          onChange={e => setField('fj710_152', e.target.value)}
          disabled={!isEditing}
          placeholder="單位"
          style={{ width: 60 }}
        />
      </div>

      <label>預留日期</label>
      <input
        type="date"
        value={dateToInput(getField('fj710_24') as string)}
        onChange={e => setField('fj710_24', inputToDate(e.target.value) as string)}
        disabled={!isEditing}
        style={{ width: 140 }}
      />

      {/* Row 5: Estimated Sell Price */}
      <label className="required">業務預估售價</label>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
        <div className="lookup-field">
          <input
            type="text"
            value={getField('fj710_161') as string || ''}
            onChange={e => setField('fj710_161', e.target.value)}
            onBlur={() => resolveSingleName('sellCurrency', 'currency', getField('fj710_161') as string)}
            disabled={!isEditing}
            placeholder="幣別"
            style={{ width: 50 }}
          />
          <span className="lookup-name">{names.sellCurrency || ''}</span>
        </div>
        <input
          type="number"
          value={getField('fj710_162') as number || 0}
          onChange={e => setField('fj710_162', parseFloat(e.target.value) || 0)}
          disabled={!isEditing}
          placeholder="售價"
          style={{ width: 100 }}
        />
        <input
          type="text"
          value={getField('fj710_163') as string || ''}
          onChange={e => setField('fj710_163', e.target.value)}
          disabled={!isEditing}
          placeholder="單位"
          style={{ width: 60 }}
        />
      </div>

      {/* Row 6: Estimated Cost */}
      <label className="required">業務預估成本</label>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
        <div className="lookup-field">
          <input
            type="text"
            value={getField('fj710_171') as string || ''}
            onChange={e => setField('fj710_171', e.target.value)}
            onBlur={() => resolveSingleName('costCurrency', 'currency', getField('fj710_171') as string)}
            disabled={!isEditing}
            placeholder="幣別"
            style={{ width: 50 }}
          />
          <span className="lookup-name">{names.costCurrency || ''}</span>
        </div>
        <input
          type="number"
          value={getField('fj710_172') as number || 0}
          onChange={e => setField('fj710_172', parseFloat(e.target.value) || 0)}
          disabled={!isEditing}
          placeholder="成本"
          style={{ width: 100 }}
        />
        <input
          type="text"
          value={getField('fj710_173') as string || ''}
          onChange={e => setField('fj710_173', e.target.value)}
          disabled={!isEditing}
          placeholder="單位"
          style={{ width: 60 }}
        />
      </div>

      {/* Row 7: Estimated annual demand + dev cost + dev quantity */}
      <label>預估年需要量</label>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
        <input
          type="number"
          value={getField('fj710_181') as number || 0}
          onChange={e => setField('fj710_181', parseFloat(e.target.value) || 0)}
          disabled={!isEditing}
          style={{ width: 100 }}
        />
        <input
          type="text"
          value={getField('fj710_182') as string || ''}
          onChange={e => setField('fj710_182', e.target.value)}
          disabled={!isEditing}
          placeholder="單位"
          style={{ width: 60 }}
        />
      </div>

      <label>預估開發費用</label>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
        <div className="lookup-field">
          <input
            type="text"
            value={getField('fj710_191') as string || ''}
            onChange={e => setField('fj710_191', e.target.value)}
            onBlur={() => resolveSingleName('extraCurrency', 'currency', getField('fj710_191') as string)}
            disabled={!isEditing}
            placeholder="幣別"
            style={{ width: 50 }}
          />
          <span className="lookup-name">{names.extraCurrency || ''}</span>
        </div>
        <input
          type="number"
          value={getField('fj710_192') as number || 0}
          onChange={e => setField('fj710_192', parseFloat(e.target.value) || 0)}
          disabled={!isEditing}
          style={{ width: 100 }}
        />
      </div>

      {/* Row 8 */}
      <label>預估開發數量</label>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
        <input
          type="number"
          value={getField('fj710_201') as number || 0}
          onChange={e => setField('fj710_201', parseFloat(e.target.value) || 0)}
          disabled={!isEditing}
          style={{ width: 100 }}
        />
        <input
          type="text"
          value={getField('fj710_202') as string || ''}
          onChange={e => setField('fj710_202', e.target.value)}
          disabled={!isEditing}
          placeholder="單位"
          style={{ width: 60 }}
        />
      </div>

      {/* Row 9: R&D Reply (BR-V12/BR-F12) */}
      <label>研發回覆代號</label>
      <div className="lookup-field">
        <input
          type="text"
          value={getField('fj710_251') as string || ''}
          onChange={e => setField('fj710_251', e.target.value)}
          onBlur={() => resolveSingleName('rdReplyCode', 'remark', getField('fj710_251') as string)}
          disabled={!isEditing}
          style={{ width: 80 }}
        />
        <span className="lookup-name">{names.rdReplyCode || ''}</span>
      </div>
    </div>

    {/* R&D Reply Content (BR-U01: edtFj710_252 always disabled) */}
    <div style={{ marginTop: 12 }}>
      <label>研發回覆內容</label>
      <textarea
        value={getField('fj710_252') as string || ''}
        disabled
        rows={3}
        style={{ width: '100%', marginTop: 4 }}
      />
    </div>
  </div>
));

Tab1BasicInfo.displayName = 'Tab1BasicInfo';

export default Tab1BasicInfo;
